import {User} from '@hconnect/apiclient'
import {LabelRegular} from '@hm/fundamental'
import React, {useEffect} from 'react'
import {UseFormReturn} from 'react-hook-form'

import {useLoggedInUserProfile} from '../../hooks/useLoggedInUser'
import {useUserInformationFormData} from '../UserInformation/hooks/useUserInformationFormData'
import {BlocklistNotificationsInput} from '../UserInformation/inputs/BlocklistNotificationsInput'
import {TestUserInput} from '../UserInformation/inputs/TestUserInput'
import {UserBrandingInput} from '../UserInformation/inputs/UserBrandingInput'
import {UserCompanyInput} from '../UserInformation/inputs/UserCompanyInput'
import {UserCountryInput} from '../UserInformation/inputs/UserCountryInput'
import {UserEmailInput} from '../UserInformation/inputs/UserEmailInput'
import {UserLanguageInput} from '../UserInformation/inputs/UserLanguageInput'
import {UserMarketInput} from '../UserInformation/inputs/UserMarketInput'
import {UserMobileNumberInput} from '../UserInformation/inputs/UserMobileNumberInput'
import {UserNameInput} from '../UserInformation/inputs/UserNameInput'
import {UserOrgUnitInput} from '../UserInformation/inputs/UserOrgUnitInput'
import {UserInformationFormData} from '../UserInformation/UserInformationForm.types'

type GeneralInformationFormProps = {
  formMethods: UseFormReturn<UserInformationFormData, object>
  isReadonly: boolean
  isLoading: boolean
  onSubmit: (data: UserInformationFormData) => void
  canEditUserEmailPhone: boolean
  user?: User | null
  loggedInUserProfile?: User | null
}
export const GeneralInformationForm = ({
  formMethods,
  isReadonly,
  isLoading,
  onSubmit,
  canEditUserEmailPhone,
  user,
  loggedInUserProfile
}: GeneralInformationFormProps) => {
  useEffect(() => {
    formMethods.reset()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReadonly])
  const values = formMethods.watch()
  const loggedInUserProfileData = useLoggedInUserProfile(loggedInUserProfile)

  const {
    brandings,
    countriesIds,
    createdByLink,
    createdByText,
    createdInText,
    createdOnText,
    createdViaText,
    marketName,
    marketSelectValues,
    primaryOrgUnitsMarketsSelectValues,
    primaryOrganisationalUnitLabel,
    selectedCountry
  } = useUserInformationFormData(values, user)

  return (
    <form className="flex flex-col gap-4 w-full" onSubmit={formMethods.handleSubmit(onSubmit)}>
      <UserNameInput
        name="name"
        readonlyMode={isReadonly}
        formMethods={formMethods}
        isLoading={isLoading}
      />
      <UserEmailInput
        readonlyMode={isReadonly}
        formMethods={formMethods}
        canEditUserEmailPhone={canEditUserEmailPhone}
        isLoading={isLoading}
        name="eMail"
      />
      <UserMobileNumberInput
        readonlyMode={isReadonly}
        formMethods={formMethods}
        canEditUserEmailPhone={canEditUserEmailPhone}
        isLoading={isLoading}
        name="mobileNumber"
      />
      <div className={`border-1px border-border_highlight_10 mx-[${isReadonly ? '12px' : 0}]`} />
      <UserCountryInput
        readonlyMode={isReadonly}
        formMethods={formMethods}
        countriesIds={countriesIds}
        isLoading={isLoading}
        selectedCountry={selectedCountry}
        name="country"
      />
      <UserMarketInput
        readonlyMode={isReadonly}
        formMethods={formMethods}
        marketName={marketName}
        marketSelectValues={marketSelectValues}
        isLoading={isLoading}
        name="marketId"
      />
      <UserOrgUnitInput
        readonlyMode={isReadonly}
        formMethods={formMethods}
        primaryOrganisationalUnitLabel={primaryOrganisationalUnitLabel}
        primaryOrgUnitsMarketsSelectValues={primaryOrgUnitsMarketsSelectValues}
        isLoading={isLoading}
        name="primaryOrganisationalUnit"
      />
      <UserBrandingInput
        readonlyMode={isReadonly}
        formMethods={formMethods}
        brandings={brandings}
        user={user}
        isLoading={isLoading}
        name="defaultBranding"
      />
      <UserLanguageInput
        readonlyMode={isReadonly}
        formMethods={formMethods}
        selectedCountry={selectedCountry}
        isLoading={isLoading}
        name="defaultLocale"
      />
      <div className={`border-1px border-border_highlight_10 mx-[${isReadonly ? '12px' : 0}]`} />
      <UserCompanyInput
        readonlyMode={isReadonly}
        formMethods={formMethods}
        isLoading={isLoading}
        name="companyName"
      />
      <BlocklistNotificationsInput
        readonlyMode={isReadonly}
        formMethods={formMethods}
        isLoading={isLoading}
        name="blocklistNotifications"
        showNewUserProfile={true}
      />
      {!isReadonly && (
        <TestUserInput
          readonlyMode={false}
          formMethods={formMethods}
          isLoading={isLoading}
          name="isTester"
          loggedInUserProfile={loggedInUserProfileData}
        />
      )}
      {isReadonly && (
        <LabelRegular className="px-12px">
          {createdOnText} {createdByText} {createdByLink} {createdViaText} {createdInText}
        </LabelRegular>
      )}
    </form>
  )
}
